import * as React from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { ChatMessageComponents } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ChatMessageComponents.tsx";
import { ThinkingIndicator } from "@/shared/components";
import {
  ChatMessage,
  ChatMessageStatus,
  ChatRecord,
} from "@/shared/domain/chat";
import {
  ChatStatus,
  isChatToolUseContent,
} from "@/shared/domain/chat/ChatRecord.ts";

import styles from "./ChatByIdView.module.css";

const AUTONAV_MAX_AGE_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Check if a message is recent enough for autonav.
 * Messages without a created timestamp (e.g., in-flight) are considered recent.
 */
function isMessageRecentForAutoNav(message: ChatMessage): boolean {
  if (!message.created) {
    return true;
  }
  const messageTime = new Date(message.created).getTime();
  const now = Date.now();
  return now - messageTime < AUTONAV_MAX_AGE_MS;
}

interface ChatByIdViewProps {
  chat: ChatRecord;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

export const ChatByIdView = ({ chat, messagesEndRef }: ChatByIdViewProps) => {
  const { handleLinkClick, autoNavEnabled } = useAIChat();
  const thinking = React.useMemo(() => {
    if (chat.messages.length === 0) {
      return true;
    }

    if (chat.status === ChatStatus.USER_TURN) {
      return false;
    }

    const lastMessage = chat.messages.at(-1);

    // If we're currently addressing a tool use request, that has its own loading indicator, so we shouldn't
    // double-display them
    if (lastMessage) {
      const lastContent = lastMessage?.content.at(-1);

      if (isChatToolUseContent(lastContent)) {
        return false;
      }
    }

    return lastMessage?.status === ChatMessageStatus.COMPLETED;
  }, [chat]);

  return (
    <>
      <div className={styles.centeringContainer}>
        <div
          className={styles.contentContainer}
          role="log"
          aria-label="Chat conversation"
          aria-live="polite"
        >
          {chat.messages.length > 0 && (
            <>
              {chat.messages.map((message, index) => (
                <ChatMessageComponents
                  key={index}
                  message={message}
                  isLastMessageInChat={index === chat.messages.length - 1}
                  onLinkClick={handleLinkClick}
                  autoNavDedupeKey={`${chat.chat_id}-${index}`}
                  autoNavEnabled={
                    autoNavEnabled && isMessageRecentForAutoNav(message)
                  }
                />
              ))}
            </>
          )}
          {/* Invisible element for auto-scroll */}
          {thinking && <ThinkingIndicator />}
        </div>
      </div>
      <div ref={messagesEndRef} />
    </>
  );
};
